import React, { useState, useEffect, useRef, useCallback } from 'react';
import lumiObserver from './lumiObserver';

// ── Theme ───────────────────────────────────────────────────────────────
const T = {
  bg: '#0D1117',
  surface: '#161B22',
  border: '#30363D',
  text: '#E6EDF3',
  textMid: '#8B949E',
  accent: '#0B8A7E',
  correct: '#10B981',
  wrong: '#EF4444',
  hint: '#F59E0B',
};

const MAX_ATTEMPTS = 3;

// ── Success evaluator ────────────────────────────────────────────────────
function evalSuccess(cond, state) {
  if (!cond || typeof cond !== 'object') return false;
  if (cond.type === 'parameter_within') {
    const v = state[cond.parameter];
    return v != null && Math.abs(v - cond.target) <= (cond.tolerance ?? 0);
  }
  if (cond.type === 'observable_within') {
    const v = state.observables?.[cond.observable];
    return v != null && Math.abs(v - cond.target) <= (cond.tolerance ?? 0);
  }
  if (cond.type === 'observable_maximized') {
    const v = state[cond.parameter];
    return v != null && Math.abs(v - cond.optimum) <= (cond.tolerance ?? 2);
  }
  return false;
}

// ── Hint evaluator ──────────────────────────────────────────────────────
function evalHintCondition(cond, state) {
  if (!cond || typeof cond !== 'string') return false;
  try {
    const flat = { ...state, ...(state.observables || {}) };
    const keys = Object.keys(flat);
    const vals = Object.values(flat);
    // eslint-disable-next-line no-new-func
    return !!new Function(...keys, `return (${cond});`)(...vals);
  } catch (_) { return false; }
}

// ── Shared slider ──────────────────────────────────────────────────────
function Slider({ label, value, min, max, step = 1, unit = '', onChange, disabled }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textMid }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: T.accent }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        disabled={disabled} onChange={e => onChange(Number(e.target.value))}
        aria-label={label}
        style={{ width: '100%', accentColor: T.accent }} />
    </div>
  );
}

// ── Feedback panel (shared with Manipulative style) ────────────────────
function FeedbackPanel({ status, message, attempts }) {
  if (status === 'idle' || !message) return null;
  const color = status === 'correct' ? T.correct : status === 'wrong' || status === 'gave_up' ? T.wrong : T.hint;
  const icon = status === 'correct' ? '✓' : status === 'gave_up' || status === 'wrong' ? '✗' : '💡';
  return (
    <div role="status" aria-live="polite" style={{
      marginTop: 12, padding: '10px 14px', borderRadius: 10,
      background: color + '22', border: `1px solid ${color}`, color,
      fontSize: 13, fontWeight: 600, lineHeight: 1.5,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
    }}>
      <span>{icon} {message}</span>
      {status === 'wrong' && attempts > 0 && (
        <span style={{ opacity: 0.75, fontSize: 11, whiteSpace: 'nowrap' }}>
          {attempts} of {MAX_ATTEMPTS} attempts
        </span>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SIMULATION 1 — PROJECTILE LAUNCHER
// ══════════════════════════════════════════════════════════════════════════
function ProjectileLauncher({ parameters, onStateChange, disabled, onCheckTrigger }) {
  const paramDef = (name, def) => parameters?.find(p => p.name === name) || def;
  const Pangle = paramDef('angle', { name: 'angle', min: 0, max: 90, default: 45, unit: '°' });
  const Pvel = paramDef('velocity', { name: 'velocity', min: 5, max: 50, default: 25, unit: ' m/s' });
  const Pg = paramDef('gravity', { name: 'gravity', min: 1, max: 20, default: 9.8, unit: ' m/s²' });

  const [angle, setAngle] = useState(Pangle.default);
  const [velocity, setVelocity] = useState(Pvel.default);
  const [gravity, setGravity] = useState(Pg.default);
  const [fired, setFired] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const startRef = useRef(0);

  const rad = angle * Math.PI / 180;
  const range = (velocity * velocity * Math.sin(2 * rad)) / gravity;
  const maxH = (velocity * velocity * Math.pow(Math.sin(rad), 2)) / (2 * gravity);
  const flight = (2 * velocity * Math.sin(rad)) / gravity;

  const W = 480, H = 240, groundY = H - 30, launchX = 30;
  const scale = Math.min(1, (W - launchX - 20) / Math.max(range, 1));

  // Expose current state so parent can evaluate on Check
  useEffect(() => {
    onStateChange?.({ angle, velocity, gravity, range, max_height: maxH, flight_time: flight, observables: { range, max_height: maxH, flight_time: flight } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [angle, velocity, gravity]);

  const draw = useCallback((tSim) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = T.surface; ctx.fillRect(0, groundY, W, H - groundY);
    ctx.strokeStyle = T.border; ctx.lineWidth = 1;

    // Distance markers with collision-proof spacing: compute a step that
    // leaves at least ~45px between labels on screen.
    const minPx = 45;
    const minMetres = Math.max(1, Math.ceil((minPx / Math.max(scale, 0.01)) / 5) * 5);
    const maxD = Math.max(range * 1.2, minMetres);
    for (let d = minMetres; d <= maxD; d += minMetres) {
      const x = launchX + d * scale;
      if (x < W - 10) {
        ctx.beginPath(); ctx.moveTo(x, groundY); ctx.lineTo(x, groundY - 5); ctx.stroke();
        ctx.fillStyle = T.textMid; ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${d}m`, x, groundY + 16);
      }
    }

    // Cannon
    ctx.save();
    ctx.translate(launchX, groundY);
    ctx.rotate(-rad);
    ctx.fillStyle = T.accent;
    ctx.fillRect(0, -6, 30, 12);
    ctx.restore();

    // Predicted path
    ctx.strokeStyle = T.accent + '66'; ctx.lineWidth = 1.5;
    ctx.beginPath();
    for (let k = 0; k <= 40; k++) {
      const t = (k / 40) * flight;
      const x = launchX + (velocity * Math.cos(rad) * t) * scale;
      const y = groundY - (velocity * Math.sin(rad) * t - 0.5 * gravity * t * t) * scale;
      if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();

    if (tSim != null) {
      const t = Math.min(tSim, flight);
      const x = launchX + (velocity * Math.cos(rad) * t) * scale;
      const y = groundY - (velocity * Math.sin(rad) * t - 0.5 * gravity * t * t) * scale;
      ctx.fillStyle = T.hint;
      ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.fill();
    }
  }, [angle, velocity, gravity, range, flight, rad, scale]);

  useEffect(() => { draw(null); }, [draw]);

  const loop = (ts) => {
    if (!startRef.current) startRef.current = ts;
    const t = (ts - startRef.current) / 1000;
    draw(t);
    if (t < flight) {
      animRef.current = requestAnimationFrame(loop);
    } else {
      setFired(false); startRef.current = 0;
    }
  };

  const fire = () => {
    if (disabled) return;
    setFired(true); startRef.current = 0;
    animRef.current = requestAnimationFrame(loop);
  };
  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  return (
    <div>
      <canvas ref={canvasRef} width={W} height={H}
        aria-label={`Projectile simulation at ${angle} degrees, ${velocity} m/s, gravity ${gravity}`}
        style={{ display: 'block', width: '100%', maxWidth: W, margin: '0 auto', background: T.bg, borderRadius: 8 }} />
      <div style={{ marginTop: 10 }}>
        <Slider label="Angle" value={angle} min={Pangle.min} max={Pangle.max} step={1} unit="°" onChange={setAngle} disabled={disabled} />
        <Slider label="Velocity" value={velocity} min={Pvel.min} max={Pvel.max} step={1} unit=" m/s" onChange={setVelocity} disabled={disabled} />
        <Slider label="Gravity" value={gravity} min={Pg.min} max={Pg.max} step={0.1} unit=" m/s²" onChange={setGravity} disabled={disabled} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10, fontSize: 12 }}>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>RANGE</div>
          <div style={{ color: T.correct, fontWeight: 800 }}>{range.toFixed(1)} m</div>
        </div>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>MAX HEIGHT</div>
          <div style={{ color: T.hint, fontWeight: 800 }}>{maxH.toFixed(1)} m</div>
        </div>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>FLIGHT TIME</div>
          <div style={{ color: T.accent, fontWeight: 800 }}>{flight.toFixed(2)} s</div>
        </div>
      </div>
      <button onClick={fire} disabled={fired || disabled}
        style={{ width: '100%', marginTop: 10, padding: 10,
          background: (fired || disabled) ? T.border : T.accent,
          color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700,
          cursor: (fired || disabled) ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1 }}>
        {fired ? 'Firing…' : '🚀 Fire'}
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SIMULATION 2 — PROBABILITY EXPERIMENT
//   Real device differentiation: die (N-sided), coin (H/T), spinner (wheel)
// ══════════════════════════════════════════════════════════════════════════
function ProbabilityExperiment({ parameters, onStateChange, disabled }) {
  const device = parameters?.find(p => p.name === 'device')?.default || 'die';
  const sides = device === 'coin' ? 2 : (parameters?.find(p => p.name === 'sides')?.default || 6);
  const labels = device === 'coin'
    ? ['Heads', 'Tails']
    : device === 'spinner'
      ? Array.from({ length: sides }, (_, i) => String.fromCharCode(65 + i)) // A, B, C…
      : Array.from({ length: sides }, (_, i) => String(i + 1));

  const [trials, setTrials] = useState(10);
  const [counts, setCounts] = useState(() => new Array(sides).fill(0));
  const [spinnerAngle, setSpinnerAngle] = useState(0);
  const [spinning, setSpinning] = useState(false);

  useEffect(() => { setCounts(new Array(sides).fill(0)); }, [sides]);

  const totalRolls = counts.reduce((a, b) => a + b, 0);
  const theoretical = 1 / sides;
  const maxDev = totalRolls === 0 ? 0 : Math.max(...counts.map(c => Math.abs(c / totalRolls - theoretical)));

  useEffect(() => {
    onStateChange?.({ trials: totalRolls, max_deviation: maxDev, theoretical, counts, value: totalRolls, sides });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalRolls, maxDev, sides]);

  const run = () => {
    if (disabled || spinning) return;
    const rolls = new Array(sides).fill(0);
    let lastRoll = 0;
    for (let i = 0; i < trials; i++) {
      const r = Math.floor(Math.random() * sides);
      rolls[r]++; lastRoll = r;
    }
    setCounts(counts.map((c, i) => c + rolls[i]));
    // Animate the spinner if applicable
    if (device === 'spinner' || device === 'coin') {
      setSpinning(true);
      const finalAngle = spinnerAngle + 720 + (lastRoll / sides) * 360;
      setSpinnerAngle(finalAngle);
      setTimeout(() => setSpinning(false), 700);
    }
  };

  const reset = () => {
    if (disabled) return;
    setCounts(new Array(sides).fill(0));
  };

  const maxCount = Math.max(1, ...counts);
  const barW = 360;

  return (
    <div>
      {/* Device visualisation */}
      {device === 'coin' && (
        <div style={{ height: 80, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: `linear-gradient(135deg, #F4C430, #B8860B)`,
            border: '3px solid #8B6508',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 900, color: '#3A2A00',
            transform: `rotateY(${spinnerAngle}deg)`,
            transformStyle: 'preserve-3d', transition: spinning ? 'transform 0.7s cubic-bezier(.2,.8,.2,1)' : 'none',
          }}>{counts.some(c => c > 0) ? (counts[0] >= counts[1] ? 'H' : 'T') : '—'}</div>
        </div>
      )}
      {device === 'spinner' && (
        <div style={{ height: 120, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <svg width={110} height={110} viewBox="0 0 110 110" aria-label={`Spinner with ${sides} segments`}>
            <g transform={`translate(55,55) rotate(${spinnerAngle})`} style={{ transition: spinning ? 'transform .7s cubic-bezier(.2,.8,.2,1)' : 'none' }}>
              {Array.from({ length: sides }).map((_, i) => {
                const a0 = (i / sides) * 2 * Math.PI - Math.PI / 2;
                const a1 = ((i + 1) / sides) * 2 * Math.PI - Math.PI / 2;
                const r = 50;
                const x0 = r * Math.cos(a0), y0 = r * Math.sin(a0);
                const x1 = r * Math.cos(a1), y1 = r * Math.sin(a1);
                const colors = ['#0B8A7E', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#14B8A6', '#EC4899', '#F97316'];
                return <path key={i} d={`M 0,0 L ${x0},${y0} A ${r} ${r} 0 0 1 ${x1},${y1} Z`} fill={colors[i % colors.length]} stroke="#fff" strokeWidth={1} />;
              })}
            </g>
            {/* Pointer */}
            <polygon points="55,0 48,12 62,12" fill={T.text} />
          </svg>
        </div>
      )}

      {/* Histogram */}
      <svg width={barW} height={180} role="img" aria-label={`Histogram of ${totalRolls} trials`} style={{ display: 'block', margin: '0 auto' }}>
        {counts.map((c, i) => {
          const w = (barW - 20) / sides - 4;
          const h = (c / maxCount) * 140;
          const x = 10 + i * (w + 4);
          return (
            <g key={i}>
              <rect x={x} y={160 - h} width={w} height={h} fill={T.accent} />
              <text x={x + w / 2} y={175} textAnchor="middle" fill={T.textMid} fontSize={10}>{labels[i]}</text>
              {c > 0 && <text x={x + w / 2} y={155 - h} textAnchor="middle" fill={T.text} fontSize={10} fontWeight={700}>{c}</text>}
            </g>
          );
        })}
        <line x1={10} y1={160} x2={barW - 10} y2={160} stroke={T.border} />
      </svg>
      <div style={{ textAlign: 'center', fontSize: 12, color: T.textMid, marginTop: 4 }}>
        Trials: {totalRolls} · Theoretical: {(theoretical * 100).toFixed(1)}% each
        {totalRolls > 0 && <> · Max empirical deviation: {(maxDev * 100).toFixed(1)}%</>}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[1, 10, 100, 1000].map(n => (
          <button key={n} onClick={() => setTrials(n)} disabled={disabled}
            style={{
              padding: '6px 12px', borderRadius: 6, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12,
              background: trials === n ? T.accent : T.surface, color: trials === n ? '#fff' : T.textMid,
              border: `1px solid ${trials === n ? T.accent : T.border}`, opacity: disabled ? 0.5 : 1,
            }}>+{n}</button>
        ))}
        <button onClick={run} disabled={disabled || spinning}
          style={{ padding: '6px 14px', background: T.correct, color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: (disabled || spinning) ? 'not-allowed' : 'pointer', fontSize: 12 }}>
          Run
        </button>
        <button onClick={reset} disabled={disabled}
          style={{ padding: '6px 12px', background: T.bg, color: T.textMid, border: `1px solid ${T.border}`, borderRadius: 6, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12 }}>
          Reset
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SIMULATION 3 — STATISTICS SAMPLER
// ══════════════════════════════════════════════════════════════════════════
function StatisticsSampler({ parameters, onStateChange, disabled }) {
  const [data, setData] = useState(parameters?.find(p => p.name === 'initial')?.default || [3, 5, 7, 7, 9, 12]);
  const svgRef = useRef(null);

  const mean = data.length ? data.reduce((a, b) => a + b, 0) / data.length : 0;
  const sorted = [...data].sort((a, b) => a - b);
  const median = data.length === 0 ? 0 : (data.length % 2 === 0
    ? (sorted[data.length / 2 - 1] + sorted[data.length / 2]) / 2
    : sorted[Math.floor(data.length / 2)]);
  const countsByVal = {};
  data.forEach(v => countsByVal[v] = (countsByVal[v] || 0) + 1);
  const modeEntry = Object.entries(countsByVal).sort((a, b) => b[1] - a[1])[0];
  const mode = modeEntry ? Number(modeEntry[0]) : null;
  const range = data.length ? Math.max(...data) - Math.min(...data) : 0;

  useEffect(() => {
    onStateChange?.({ mean, median, mode, count: data.length, range, value: mean });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mean, median, mode, data.length, range]);

  const W = 400, H = 160, pad = 30;
  const minV = data.length ? Math.min(...data, 0) : 0;
  const maxV = data.length ? Math.max(...data, 10) : 10;
  const xFor = (v) => pad + ((v - minV) / Math.max(1, (maxV - minV))) * (W - 2 * pad);

  const addPoint = (e) => {
    if (disabled) return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    let v = minV + ((x - pad) / (W - 2 * pad)) * (maxV - minV);
    v = Math.round(v);
    if (v < 0) return;
    setData([...data, v]);
  };
  const removePoint = (i, e) => {
    if (disabled) return;
    e.stopPropagation();
    setData(data.filter((_, j) => j !== i));
  };

  return (
    <div>
      <svg ref={svgRef} width={W} height={H} onClick={addPoint}
        role="application" aria-label={`Statistics data with ${data.length} points`}
        style={{ display: 'block', margin: '0 auto', background: T.surface, borderRadius: 8, cursor: disabled ? 'default' : 'crosshair' }}>
        <line x1={pad} y1={H / 2} x2={W - pad} y2={H / 2} stroke={T.textMid} />
        {Array.from({ length: 11 }).map((_, i) => {
          const v = minV + (i / 10) * (maxV - minV);
          return (
            <g key={i}>
              <line x1={xFor(v)} y1={H / 2 - 4} x2={xFor(v)} y2={H / 2 + 4} stroke={T.textMid} />
              <text x={xFor(v)} y={H / 2 + 18} textAnchor="middle" fill={T.textMid} fontSize={10}>{v.toFixed(0)}</text>
            </g>
          );
        })}
        {data.length > 0 && (<>
          <line x1={xFor(mean)} y1={20} x2={xFor(mean)} y2={H - 20} stroke={T.correct} strokeDasharray="4 3" />
          <text x={xFor(mean)} y={16} textAnchor="middle" fill={T.correct} fontSize={11} fontWeight={700}>mean {mean.toFixed(2)}</text>
          <line x1={xFor(median)} y1={20} x2={xFor(median)} y2={H - 20} stroke={T.hint} strokeDasharray="4 3" />
          <text x={xFor(median)} y={H - 6} textAnchor="middle" fill={T.hint} fontSize={11} fontWeight={700}>median {median}</text>
        </>)}
        {data.map((v, i) => (
          <circle key={i} cx={xFor(v)} cy={H / 2} r={6} fill={T.accent} stroke="#fff" strokeWidth={1.5}
            onClick={(e) => removePoint(i, e)} style={{ cursor: disabled ? 'default' : 'pointer' }} />
        ))}
      </svg>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 10, fontSize: 11 }}>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>MEAN</div>
          <div style={{ color: T.correct, fontWeight: 800 }}>{mean.toFixed(2)}</div>
        </div>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>MEDIAN</div>
          <div style={{ color: T.hint, fontWeight: 800 }}>{median}</div>
        </div>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>MODE</div>
          <div style={{ color: T.accent, fontWeight: 800 }}>{mode ?? '—'}</div>
        </div>
        <div style={{ background: T.bg, padding: 8, borderRadius: 6, textAlign: 'center' }}>
          <div style={{ color: T.textMid, fontSize: 10 }}>RANGE</div>
          <div style={{ color: T.text, fontWeight: 800 }}>{range}</div>
        </div>
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: T.textMid, marginTop: 6 }}>
        Click to add a point · Click a point to remove it
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SIMULATION 4 — FUNCTION GRAPHER
// ══════════════════════════════════════════════════════════════════════════
function FunctionGrapher({ parameters, onStateChange, disabled }) {
  const family = parameters?.find(p => p.name === 'family')?.default || 'quadratic';
  const [a, setA] = useState(1);
  const [b, setB] = useState(0);
  const [c, setC] = useState(0);

  const fn = (x) => {
    if (family === 'linear') return a * x + b;
    if (family === 'sine') return a * Math.sin(b * x) + c;
    return a * x * x + b * x + c;
  };

  let roots = [], vertex = null;
  if (family === 'quadratic' && a !== 0) {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const s = Math.sqrt(disc);
      roots = [(-b - s) / (2 * a), (-b + s) / (2 * a)];
    }
    vertex = [-b / (2 * a), c - (b * b) / (4 * a)];
  } else if (family === 'linear' && a !== 0) {
    roots = [-b / a];
  }

  useEffect(() => {
    onStateChange?.({ a, b, c, family, roots, vertex, value: a });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a, b, c]);

  const W = 400, H = 280, pad = 20;
  const xMin = -8, xMax = 8, yMin = -8, yMax = 8;
  const xFor = (x) => pad + ((x - xMin) / (xMax - xMin)) * (W - 2 * pad);
  const yFor = (y) => pad + ((yMax - y) / (yMax - yMin)) * (H - 2 * pad);

  const pts = [];
  for (let i = 0; i <= 200; i++) {
    const x = xMin + (i / 200) * (xMax - xMin);
    let y = fn(x);
    if (!isFinite(y)) continue;
    y = Math.max(yMin - 10, Math.min(yMax + 10, y));
    pts.push(`${xFor(x).toFixed(2)},${yFor(y).toFixed(2)}`);
  }

  return (
    <div>
      <svg width={W} height={H} role="img" aria-label={`Graph of ${family} function`} style={{ display: 'block', margin: '0 auto', background: T.surface, borderRadius: 8 }}>
        {Array.from({ length: 17 }).map((_, i) => {
          const x = xMin + i;
          return <line key={'v' + i} x1={xFor(x)} y1={pad} x2={xFor(x)} y2={H - pad} stroke={T.border} strokeWidth={x === 0 ? 1.5 : 0.4} />;
        })}
        {Array.from({ length: 17 }).map((_, i) => {
          const y = yMin + i;
          return <line key={'h' + i} x1={pad} y1={yFor(y)} x2={W - pad} y2={yFor(y)} stroke={T.border} strokeWidth={y === 0 ? 1.5 : 0.4} />;
        })}
        <polyline points={pts.join(' ')} fill="none" stroke={T.accent} strokeWidth={2.5} />
        {roots.filter(r => isFinite(r) && r >= xMin && r <= xMax).map((r, i) => (
          <circle key={i} cx={xFor(r)} cy={yFor(0)} r={5} fill={T.hint} stroke="#fff" strokeWidth={1.5} />
        ))}
        {vertex && vertex[0] >= xMin && vertex[0] <= xMax && vertex[1] >= yMin && vertex[1] <= yMax && (
          <circle cx={xFor(vertex[0])} cy={yFor(vertex[1])} r={5} fill={T.correct} stroke="#fff" strokeWidth={1.5} />
        )}
      </svg>
      <div style={{ textAlign: 'center', fontSize: 13, marginTop: 6, color: T.text, fontWeight: 700 }}>
        {family === 'linear' && `y = ${a}x + ${b}`}
        {family === 'quadratic' && `y = ${a}x² + ${b}x + ${c}`}
        {family === 'sine' && `y = ${a}·sin(${b}x) + ${c}`}
      </div>
      <div style={{ marginTop: 10 }}>
        <Slider label="a" value={a} min={-5} max={5} step={0.5} onChange={setA} disabled={disabled} />
        <Slider label="b" value={b} min={-5} max={5} step={0.5} onChange={setB} disabled={disabled} />
        {family !== 'linear' && <Slider label="c" value={c} min={-5} max={5} step={0.5} onChange={setC} disabled={disabled} />}
      </div>
      {family === 'quadratic' && vertex && (
        <div style={{ fontSize: 11, color: T.textMid, textAlign: 'center', marginTop: 4 }}>
          Vertex: ({vertex[0].toFixed(2)}, {vertex[1].toFixed(2)}) · Roots: {roots.length ? roots.map(r => r.toFixed(2)).join(', ') : 'none (real)'}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// SIMULATION 5 — TRANSFORMATION SANDBOX
// ══════════════════════════════════════════════════════════════════════════
function TransformationSandbox({ parameters, onStateChange, disabled }) {
  const initial = parameters?.find(p => p.name === 'shape')?.default || [[1, 1], [3, 1], [3, 3], [1, 3]];
  const [shape, setShape] = useState(initial);
  const [before] = useState(initial);

  useEffect(() => {
    onStateChange?.({ shape, transformed: JSON.stringify(shape) !== JSON.stringify(before), value: 1 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape]);

  const W = 360, H = 360, pad = 20;
  const xMin = -10, xMax = 10, yMin = -10, yMax = 10;
  const xFor = (x) => pad + ((x - xMin) / (xMax - xMin)) * (W - 2 * pad);
  const yFor = (y) => pad + ((yMax - y) / (yMax - yMin)) * (H - 2 * pad);

  const apply = (fn) => { if (!disabled) setShape(shape.map(fn)); };
  const reset = () => { if (!disabled) setShape(before); };
  const toPoly = (arr) => arr.map(([x, y]) => `${xFor(x)},${yFor(y)}`).join(' ');

  return (
    <div>
      <svg width={W} height={H} role="img" aria-label="Shape transformation grid" style={{ display: 'block', margin: '0 auto', background: T.surface, borderRadius: 8 }}>
        {Array.from({ length: 21 }).map((_, i) => {
          const x = xMin + i;
          return <line key={'v' + i} x1={xFor(x)} y1={pad} x2={xFor(x)} y2={H - pad} stroke={T.border} strokeWidth={x === 0 ? 1.5 : 0.3} />;
        })}
        {Array.from({ length: 21 }).map((_, i) => {
          const y = yMin + i;
          return <line key={'h' + i} x1={pad} y1={yFor(y)} x2={W - pad} y2={yFor(y)} stroke={T.border} strokeWidth={y === 0 ? 1.5 : 0.3} />;
        })}
        <polygon points={toPoly(before)} fill="none" stroke={T.textMid} strokeWidth={1} strokeDasharray="4 3" />
        <polygon points={toPoly(shape)} fill={T.accent + '44'} stroke={T.accent} strokeWidth={2} style={{ transition: 'all .3s' }} />
      </svg>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, marginTop: 10 }}>
        <button onClick={() => apply(([x, y]) => [x + 1, y])} disabled={disabled} style={btn(disabled)}>→ +1x</button>
        <button onClick={() => apply(([x, y]) => [x, y + 1])} disabled={disabled} style={btn(disabled)}>↑ +1y</button>
        <button onClick={() => apply(([x, y]) => [x - 1, y])} disabled={disabled} style={btn(disabled)}>← −1x</button>
        <button onClick={() => apply(([x, y]) => [x, y - 1])} disabled={disabled} style={btn(disabled)}>↓ −1y</button>
        <button onClick={() => apply(([x, y]) => [y, -x])} disabled={disabled} style={btn(disabled)}>↻ 90°</button>
        <button onClick={() => apply(([x, y]) => [x, -y])} disabled={disabled} style={btn(disabled)}>flip x</button>
        <button onClick={() => apply(([x, y]) => [-x, y])} disabled={disabled} style={btn(disabled)}>flip y</button>
        <button onClick={() => apply(([x, y]) => [x * 2, y * 2])} disabled={disabled} style={btn(disabled)}>× 2</button>
        <button onClick={() => apply(([x, y]) => [x / 2, y / 2])} disabled={disabled} style={btn(disabled)}>÷ 2</button>
        <button onClick={reset} disabled={disabled} style={{ ...btn(disabled), background: T.bg, color: T.textMid, gridColumn: 'span 3' }}>Reset</button>
      </div>
    </div>
  );
}

const btn = (disabled) => ({
  padding: '8px', background: disabled ? T.border : T.accent, color: '#fff',
  border: 'none', borderRadius: 6, fontWeight: 700,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, opacity: disabled ? 0.5 : 1,
});

// ══════════════════════════════════════════════════════════════════════════
// MAIN DISPATCHER — same Check-button pattern as Manipulative
// ══════════════════════════════════════════════════════════════════════════
export default function Simulation({
  type,
  parameters = [],
  observables = [],
  prompt,
  successCondition,
  onCorrect,
  onWrongHints = [],
  feedbackOnStuck,
  onInteraction,
  onComplete,
  onGiveUp,
  interactiveId,   // optional — when provided, emits observer events
}) {
  const [state, setState] = useState({});
  const [status, setStatus] = useState('idle');
  const [attempts, setAttempts] = useState(0);
  const [message, setMessage] = useState('');

  const handleStateChange = useCallback((newState) => {
    setState(newState);
    onInteraction?.(newState);
    if (interactiveId) {
      lumiObserver.emit({ interactiveId, type: 'slider_moved', payload: { state: newState, simulationType: type } });
    }
    if (status === 'wrong') setStatus('idle');
  }, [onInteraction, status, interactiveId, type]);

  const handleCheck = () => {
    if (status === 'correct' || status === 'gave_up') return;
    if (evalSuccess(successCondition, state)) {
      setStatus('correct');
      setMessage(onCorrect || 'Nicely done.');
      onComplete?.(state);
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'completed', payload: { state, simulationType: type } });
      }
      return;
    }
    const next = attempts + 1;
    setAttempts(next);
    let nudge = '';
    for (const h of onWrongHints) {
      if (evalHintCondition(h.condition, state)) { nudge = h.hint; break; }
    }
    if (next >= MAX_ATTEMPTS) {
      setStatus('gave_up');
      setMessage(feedbackOnStuck || "Let's come back to this one later.");
      onGiveUp?.(state);
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'gave_up', payload: { state, attempts: next } });
      }
    } else {
      setStatus('wrong');
      setMessage(nudge || (next === 1 ? 'Not quite — keep experimenting.' : 'Close — try adjusting one parameter at a time.'));
      if (interactiveId) {
        lumiObserver.emit({ interactiveId, type: 'check_wrong', payload: { state, attempt: next, hint: nudge } });
      }
    }
  };

  const done = status === 'correct' || status === 'gave_up';
  const common = { parameters, observables, onStateChange: handleStateChange, disabled: done };

  const body = (() => {
    switch (type) {
      case 'projectile':             return <ProjectileLauncher {...common} />;
      case 'probability_experiment': return <ProbabilityExperiment {...common} />;
      case 'statistics_sampler':     return <StatisticsSampler {...common} />;
      case 'function_grapher':       return <FunctionGrapher {...common} />;
      case 'transformation_sandbox': return <TransformationSandbox {...common} />;
      default: return <div style={{ color: T.wrong }}>Unknown simulation type: {type}</div>;
    }
  })();

  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14,
      padding: 16, fontFamily: "'Plus Jakarta Sans', sans-serif", color: T.text,
    }}>
      {prompt && (
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{prompt}</div>
      )}
      {body}
      <FeedbackPanel status={status} message={message} attempts={attempts} />
      {!done && (
        <button onClick={handleCheck}
          style={{
            display: 'block', width: '100%', marginTop: 12, padding: '10px',
            background: T.accent, color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}>
          Check answer
        </button>
      )}
    </div>
  );
}
